// ZIP-pakking i nettleseren.
//
// Brukes når flere filer skal lastes ned samtidig - typisk valgte bilag i
// innboksen. Alternativet var en Cloud Function som pakket på serveren, men
// filene ligger allerede tilgjengelige for nettleseren via Storage-lenkene, så
// en runde innom backend ville bare kostet tid og penger.
//
// Arkivet skrives uten komprimering (metode 0, «stored»). Innholdet er PDF-er
// og bilder som allerede er komprimert, så deflate ville spart nesten ingenting
// og krevd et eksternt bibliotek. Zip64 er utelatt av samme grunn: et
// bilagsutvalg i innboksen kommer ikke i nærheten av 4 GB.
(function initZipArchive(global) {
  const tr = (key) => (typeof global.t === 'function' ? global.t(key) : key);
  const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let index = 0; index < 256; index += 1) {
      let value = index;
      for (let bit = 0; bit < 8; bit += 1) value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
      table[index] = value >>> 0;
    }
    return table;
  })();

  const crc32 = (bytes) => {
    let crc = 0xffffffff;
    for (let index = 0; index < bytes.length; index += 1) {
      crc = CRC_TABLE[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
  };

  const encodeName = (value) => new TextEncoder().encode(value);

  // Filnavnet i arkivet må tåle å bli pakket ut på Windows: ingen katalogskiller
  // og ingen tegn Utforsker nekter å skrive.
  const safeEntryName = (value, fallback) => {
    const cleaned = String(value == null ? '' : value)
      .replace(/[\\/]+/g, '-')
      .replace(/[\x00-\x1f<>:"|?*]+/g, '')
      .replace(/^\.+/, '')
      .trim()
      .slice(0, 150);
    return cleaned || fallback || 'fil';
  };

  const splitExtension = (name) => {
    const dot = name.lastIndexOf('.');
    return dot > 0 ? { base: name.slice(0, dot), ext: name.slice(dot) } : { base: name, ext: '' };
  };

  // To bilag kan begge hete «faktura.pdf». Zip tillater duplikate navn, men da
  // overskriver utpakkingen den ene, så navnene gjøres unike her.
  const uniqueEntryNames = (names) => {
    const used = new Set();
    return (Array.isArray(names) ? names : []).map((raw, index) => {
      const name = safeEntryName(raw, `fil-${index + 1}`);
      if (!used.has(name.toLowerCase())) {
        used.add(name.toLowerCase());
        return name;
      }
      const { base, ext } = splitExtension(name);
      let counter = 2;
      let candidate = `${base} (${counter})${ext}`;
      while (used.has(candidate.toLowerCase())) {
        counter += 1;
        candidate = `${base} (${counter})${ext}`;
      }
      used.add(candidate.toLowerCase());
      return candidate;
    });
  };

  const dosDateTime = (date) => {
    const value = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
    const year = Math.max(1980, value.getFullYear());
    return {
      time: ((value.getHours() & 0x1f) << 11) | ((value.getMinutes() & 0x3f) << 5) | ((value.getSeconds() / 2) & 0x1f),
      date: (((year - 1980) & 0x7f) << 9) | (((value.getMonth() + 1) & 0x0f) << 5) | (value.getDate() & 0x1f),
    };
  };

  const toBytes = async (data) => {
    if (data instanceof Uint8Array) return data;
    if (data instanceof ArrayBuffer) return new Uint8Array(data);
    if (data && typeof data.arrayBuffer === 'function') return new Uint8Array(await data.arrayBuffer());
    if (typeof data === 'string') return new TextEncoder().encode(data);
    throw new Error(tr('r1e.zip.unknownContent'));
  };

  const createZipBlob = async (files, { date } = {}) => {
    const list = (Array.isArray(files) ? files : []).filter(Boolean);
    if (!list.length) throw new Error(tr('r1e.zip.noFiles'));
    const names = uniqueEntryNames(list.map((file) => file.name));
    const stamp = dosDateTime(date);

    const entries = [];
    for (let index = 0; index < list.length; index += 1) {
      const bytes = await toBytes(list[index].data);
      entries.push({ nameBytes: encodeName(names[index]), bytes, crc: crc32(bytes), offset: 0 });
    }

    const localSize = entries.reduce((sum, entry) => sum + 30 + entry.nameBytes.length + entry.bytes.length, 0);
    const centralSize = entries.reduce((sum, entry) => sum + 46 + entry.nameBytes.length, 0);
    const buffer = new Uint8Array(localSize + centralSize + 22);
    const view = new DataView(buffer.buffer);
    let offset = 0;

    entries.forEach((entry) => {
      entry.offset = offset;
      view.setUint32(offset, 0x04034b50, true);
      view.setUint16(offset + 4, 20, true);
      // Bit 11 sier at filnavnet er UTF-8. Uten den tolker utpakkeren navnet
      // som CP437, og æ, ø og å kommer ut som rare tegn.
      view.setUint16(offset + 6, 0x0800, true);
      view.setUint16(offset + 8, 0, true);
      view.setUint16(offset + 10, stamp.time, true);
      view.setUint16(offset + 12, stamp.date, true);
      view.setUint32(offset + 14, entry.crc, true);
      view.setUint32(offset + 18, entry.bytes.length, true);
      view.setUint32(offset + 22, entry.bytes.length, true);
      view.setUint16(offset + 26, entry.nameBytes.length, true);
      view.setUint16(offset + 28, 0, true);
      buffer.set(entry.nameBytes, offset + 30);
      buffer.set(entry.bytes, offset + 30 + entry.nameBytes.length);
      offset += 30 + entry.nameBytes.length + entry.bytes.length;
    });

    const centralStart = offset;
    entries.forEach((entry) => {
      view.setUint32(offset, 0x02014b50, true);
      view.setUint16(offset + 4, 20, true);
      view.setUint16(offset + 6, 20, true);
      view.setUint16(offset + 8, 0x0800, true);
      view.setUint16(offset + 10, 0, true);
      view.setUint16(offset + 12, stamp.time, true);
      view.setUint16(offset + 14, stamp.date, true);
      view.setUint32(offset + 16, entry.crc, true);
      view.setUint32(offset + 20, entry.bytes.length, true);
      view.setUint32(offset + 24, entry.bytes.length, true);
      view.setUint16(offset + 28, entry.nameBytes.length, true);
      view.setUint16(offset + 30, 0, true);
      view.setUint16(offset + 32, 0, true);
      view.setUint16(offset + 34, 0, true);
      view.setUint16(offset + 36, 0, true);
      view.setUint32(offset + 38, 0, true);
      view.setUint32(offset + 42, entry.offset, true);
      buffer.set(entry.nameBytes, offset + 46);
      offset += 46 + entry.nameBytes.length;
    });

    view.setUint32(offset, 0x06054b50, true);
    view.setUint16(offset + 4, 0, true);
    view.setUint16(offset + 6, 0, true);
    view.setUint16(offset + 8, entries.length, true);
    view.setUint16(offset + 10, entries.length, true);
    view.setUint32(offset + 12, offset - centralStart, true);
    view.setUint32(offset + 16, centralStart, true);
    view.setUint16(offset + 20, 0, true);

    return new Blob([buffer], { type: 'application/zip' });
  };

  global.PlummeZipArchive = {
    crc32,
    safeEntryName,
    uniqueEntryNames,
    createZipBlob,
  };
})(typeof window !== 'undefined' ? window : globalThis);
